import { useMemo } from 'react';
import type { JobThread } from '../types/foreman';
import type { SessionInfo, ThreadAcl } from '../types/gateway';

export type ThreadVisibility = 'private' | 'shared' | 'org';

export interface JobThreadWithAcl extends JobThread {
  visibility: ThreadVisibility;
  /** Number of OTHER users this thread is shared with (excluding owner). 0 if private. */
  shareCount: number;
  /** True if the current user is the owner. */
  ownedByMe: boolean;
}

export interface UseThreadsReturn {
  threads: JobThreadWithAcl[];
  myThreads: JobThreadWithAcl[];
  sharedWithMe: JobThreadWithAcl[];
  /** Which thread is open. This is a MIRROR of useSessions' activeSessionId —
   *  the single source of truth. Changing it goes through switchSession, never
   *  a local setter here (the old dual-state `?? activeThreadId` shadow made
   *  setActiveThreadId a silent no-op whenever a session was active). */
  activeThreadId: string | null;
}

function classify(acl: ThreadAcl | undefined): ThreadVisibility {
  if (!acl) return 'private';
  if (acl.sharedWith === '*') return 'org';
  if (Array.isArray(acl.sharedWith) && acl.sharedWith.length > 0) return 'shared';
  return 'private';
}

function shareCount(acl: ThreadAcl | undefined): number {
  if (!acl) return 0;
  if (acl.sharedWith === '*') return -1; // sentinel for org
  if (Array.isArray(acl.sharedWith)) return acl.sharedWith.length;
  return 0;
}

/** Derives thread list from sessions + ACL. Sessions without an ACL row are dropped. */
export function useThreads(
  sessions: SessionInfo[],
  acl: ThreadAcl[],
  currentUser: string | null | undefined,
  activeSessionId: string | null
): UseThreadsReturn {
  const aclByKey = useMemo(() => new Map(acl.map((a) => [a.sessionKey, a])), [acl]);

  const threads: JobThreadWithAcl[] = useMemo(() => {
    if (!currentUser) return [];
    return sessions.map((s) => {
      const a = aclByKey.get(s.id);
      const isWelcome = s.id === `welcome:${currentUser}`;
      return {
        id: s.id,
        name: a?.name || s.name || s.id,
        code: isWelcome ? 'WELCOME' : s.id === 'main' ? 'MAIN' : s.id.toUpperCase().slice(0, 12),
        status: 'active' as const,
        unread: 0,
        lastMessage: (s.metadata?.lastMessage as string) || '',
        lastMessageAt: s.lastMessageAt || s.createdAt,
        role: 'designer' as const,
        visibility: classify(a),
        shareCount: shareCount(a),
        ownedByMe: !!a && a.owner === currentUser,
      };
    });
  }, [sessions, aclByKey, currentUser]);

  const myThreads = useMemo(() => threads.filter((t) => t.ownedByMe), [threads]);
  const sharedWithMe = useMemo(() => threads.filter((t) => !t.ownedByMe), [threads]);

  return {
    threads,
    myThreads,
    sharedWithMe,
    // Single source of truth — mirrors the open session. Sidebar highlight,
    // chat pane, and compose all key off activeSessionId via this.
    activeThreadId: activeSessionId,
  };
}
